import chroma from 'chroma-js';
import type { ColorPalette, ColorSlot } from './types/theme';

// --- Helper: Ensure color is valid hex ---
// Exporting for use in other modules like useBranding
export function isValidHex(hex: string): boolean {
  return /^#([A-Fa-f0-9]{3,4}){1,2}$/.test(hex);
}

/**
 * Generate a color scale with proper contrast ratios
 */
function generateContrastScale(
  baseColor: string,
  backgroundColor: string,
  steps: number = 10
): string[] {
  const scale = chroma
    .scale([
      chroma(baseColor).brighten(2),
      baseColor,
      chroma(baseColor).darken(2),
    ])
    .mode('lab')
    .colors(steps);

  // Filter colors that meet minimum contrast requirements
  return scale.filter((color) => {
    const contrast = chroma.contrast(color, backgroundColor);
    return contrast >= 3; // Minimum for large text
  });
}

/**
 * Find a color variation that meets the target contrast ratio
 */
function ensureContrast(
  color: string,
  background: string,
  targetRatio: number = 4.5
): string {
  let currentColor = chroma(color);
  const bgLuminance = chroma(background).luminance();
  const isBackgroundDark = bgLuminance < 0.5;

  // Check if color already meets contrast requirement
  if (chroma.contrast(currentColor, background) >= targetRatio) {
    return currentColor.hex();
  }

  // Adjust the color to meet contrast requirements
  let attempts = 0;
  const maxAttempts = 20;

  while (
    chroma.contrast(currentColor, background) < targetRatio &&
    attempts < maxAttempts
  ) {
    if (isBackgroundDark) {
      // Lighten the color for dark backgrounds
      currentColor = currentColor.brighten(0.3);
    } else {
      // Darken the color for light backgrounds
      currentColor = currentColor.darken(0.3);
    }
    attempts++;
  }

  // If we couldn't achieve the target contrast, use white or black
  if (chroma.contrast(currentColor, background) < targetRatio) {
    return isBackgroundDark ? '#FFFFFF' : '#000000';
  }

  return currentColor.hex();
}

/**
 * Generate complementary colors based on the primary color
 */
function generateComplementaryColors(primaryColor: string, isDark: boolean) {
  const primary = chroma(primaryColor);

  // Generate triadic colors for variety
  const hsl = primary.hsl();
  const secondary = chroma.hsl((hsl[0] + 120) % 360, hsl[1] * 0.8, hsl[2]);
  const accent = chroma.hsl((hsl[0] + 240) % 360, hsl[1] * 0.9, hsl[2] * 1.1);

  return {
    secondary: secondary.hex(),
    accent: accent.hex(),
  };
}

/**
 * Generate semantic colors (success, warning, error)
 */
function generateSemanticColors(isDark: boolean) {
  const successBase = '#10B981';
  const warningBase = '#F59E0B';
  const errorBase = '#EF4444';

  if (isDark) {
    return {
      success: chroma(successBase).brighten(0.5).hex(),
      warning: chroma(warningBase).brighten(0.3).hex(),
      error: chroma(errorBase).brighten(0.3).hex(),
    };
  }

  return {
    success: successBase,
    warning: warningBase,
    error: errorBase,
  };
}

/**
 * Generate a complete theme palette based on a primary color
 */
export function generateThemePalette(
  primaryColorHex: string,
  isDarkMode: boolean = false
): ColorPalette {
  if (!isValidHex(primaryColorHex)) {
    console.error(
      'Invalid baseColorHex provided to generateThemePalette:',
      primaryColorHex
    );
    // Return a fallback palette
    return generateFallbackPalette(isDarkMode);
  }

  // Base colors
  const background = isDarkMode ? '#121212' : '#FFFFFF';
  const surface = isDarkMode ? '#1E1E1E' : '#F9FAFB';

  // Generate gray scale for neutral colors
  const grayScale = chroma.scale(['#FFFFFF', '#000000']).mode('lab').colors(10);

  // Ensure primary color has good contrast
  const primary = ensureContrast(primaryColorHex, background, 4.5);
  const primaryContent =
    chroma.contrast(primary, '#FFFFFF') > chroma.contrast(primary, '#000000')
      ? '#FFFFFF'
      : '#000000';

  // Generate complementary colors
  const { secondary, accent } = generateComplementaryColors(
    primary,
    isDarkMode
  );
  const secondaryAdjusted = ensureContrast(secondary, background, 4.5);
  const accentAdjusted = ensureContrast(accent, background, 4.5);

  // Generate semantic colors
  const semanticColors = generateSemanticColors(isDarkMode);

  // Build the complete palette
  const palette: ColorPalette = {
    // Primary colors
    primary,
    primaryContent,

    // Secondary colors
    secondary: secondaryAdjusted,
    secondaryContent:
      chroma.contrast(secondaryAdjusted, '#FFFFFF') >
      chroma.contrast(secondaryAdjusted, '#000000')
        ? '#FFFFFF'
        : '#000000',

    // Accent colors
    accent: accentAdjusted,
    accentContent:
      chroma.contrast(accentAdjusted, '#FFFFFF') >
      chroma.contrast(accentAdjusted, '#000000')
        ? '#FFFFFF'
        : '#000000',

    // Neutral colors
    neutral: isDarkMode ? grayScale[5] : grayScale[4],
    neutralContent: isDarkMode ? grayScale[1] : grayScale[8],

    // Background colors
    background,
    surface,
    surfaceContent: isDarkMode ? '#E0E0E0' : '#1F2937',

    // Base colors (variations of background)
    base100: isDarkMode ? grayScale[9] : grayScale[0],
    base200: isDarkMode ? grayScale[8] : grayScale[1],
    base300: isDarkMode ? grayScale[7] : grayScale[2],
    baseContent: isDarkMode ? grayScale[1] : grayScale[8],

    // Text colors
    textHeading: isDarkMode ? grayScale[0] : grayScale[9],
    textBody: isDarkMode ? grayScale[2] : grayScale[7],
    textMuted: isDarkMode ? grayScale[4] : grayScale[5],
    textLink: primary,
    textLinkHover: chroma(primary).darken(0.5).hex(),

    // Border colors
    border: isDarkMode ? grayScale[7] : grayScale[3],
    borderVariant: isDarkMode ? grayScale[6] : grayScale[2],

    // Semantic colors
    success: semanticColors.success,
    successContent: '#FFFFFF',
    warning: semanticColors.warning,
    warningContent:
      chroma.contrast(semanticColors.warning, '#FFFFFF') >
      chroma.contrast(semanticColors.warning, '#000000')
        ? '#FFFFFF'
        : '#000000',
    error: semanticColors.error,
    errorContent: '#FFFFFF',
  };

  return palette;
}

/**
 * Generate a fallback palette when invalid color is provided
 */
function generateFallbackPalette(isDarkMode: boolean): ColorPalette {
  const fallbackBg = isDarkMode ? '#121212' : '#FFFFFF';
  const fallbackText = isDarkMode ? '#FFFFFF' : '#000000';
  const fallbackPrimary = '#FF6B35';

  return {
    primary: fallbackPrimary,
    primaryContent: fallbackText,
    secondary: fallbackText,
    secondaryContent: fallbackPrimary,
    accent: fallbackText,
    accentContent: fallbackPrimary,
    neutral: '#808080',
    neutralContent: fallbackText,
    background: fallbackBg,
    surface: fallbackBg,
    surfaceContent: fallbackText,
    base100: fallbackBg,
    base200: fallbackBg,
    base300: fallbackBg,
    baseContent: fallbackText,
    border: '#808080',
    borderVariant: '#A0A0A0',
    textHeading: fallbackText,
    textBody: fallbackText,
    textMuted: '#808080',
    textLink: fallbackPrimary,
    textLinkHover: fallbackPrimary,
    success: '#22C55E',
    successContent: '#FFFFFF',
    warning: '#FACC15',
    warningContent: '#000000',
    error: '#EF4444',
    errorContent: '#FFFFFF',
  };
}
